import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useBlackjackAreaController } from '../../../../classes/TownController';
import useTownController from '../../../../hooks/useTownController';
import {
  BlackjackArea as BlackjackAreaModel,
  BlackjackPlayer,
} from '../../../../types/CoveyTownSocket';
import BlackjackArea from '../BlackjackArea';
import BlackjackLeaderboard from './BlackjackLeaderboard';

export default function NewBlackjackGameModal({
  isOpen,
  close,
  newBlackjackGame,
}: {
  isOpen: boolean;
  close: () => void;
  newBlackjackGame: BlackjackArea;
}): JSX.Element {
  const coveyTownController = useTownController();
  const blackjackController = useBlackjackAreaController(newBlackjackGame.name);
  const thisPlayer = coveyTownController.ourPlayer;
  const [bet, setBet] = useState<number>(10);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  coveyTownController.pause();
  useEffect(() => {
    if (blackjackController) {
      coveyTownController.pause();
    } else {
      coveyTownController.unPause();
    }
  }, [blackjackController, coveyTownController, newBlackjackGame]);

  const closeModal = useCallback(() => {
    if (newBlackjackGame) {
      coveyTownController.interactEnd(newBlackjackGame);
      coveyTownController.unPause();
    }
  }, [coveyTownController, newBlackjackGame]);

  const toggleLeaderboardOpen = useCallback(() => {
    setLeaderboardOpen(!leaderboardOpen);
  }, [leaderboardOpen]);

  const toast = useToast();

  const createBlackjackGame = useCallback(async () => {
    if (bet > thisPlayer.gamePoints) {
      toast({ title: 'Not enough points to bet', status: 'error' });
      return;
    }
    if (bet && bet > 0 && newBlackjackGame) {
      const blackjackGameToCreate: BlackjackAreaModel = {
        id: newBlackjackGame.name,
        isGamePlaying: false,
        blackjackPlayers: [],
        dealerHand: [],
        dealerFirstCardDown: true,
        activeTurnPlayerId: '',
      };
      try {
        await coveyTownController.createBlackjackArea(blackjackGameToCreate);
        toast({
          title: 'Blackjack Game Created! Press the start button when you are ready!',
          status: 'success',
        });
        const newPlayers = [] as BlackjackPlayer[];
        blackjackController.blackjackPlayers.forEach(eachPlayer => newPlayers.push(eachPlayer));
        newPlayers.push({
          id: thisPlayer.id,
          bet: bet,
          hand: [],
          result: undefined,
          hasBlackjack: false,
        });
        blackjackController.blackjackPlayers = newPlayers;
        thisPlayer.gamePoints -= bet;
        coveyTownController.emitBlackjackAreaUpdate(blackjackController);
        setBet(bet);
        coveyTownController.unPause();
        close();
      } catch (err) {
        if (err instanceof Error) {
          toast({
            title: 'Unable to create conversation',
            description: err.toString(),
            status: 'error',
          });
        } else {
          console.trace(err);
          toast({
            title: 'Unexpected Error',
            status: 'error',
          });
        }
      }
    } else {
      toast({ title: 'Please make a bet greater than 0', status: 'error' });
    }
  }, [bet, thisPlayer, newBlackjackGame, toast, coveyTownController, blackjackController, close]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        closeModal();
        coveyTownController.unPause();
      }}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Make a bet at {newBlackjackGame?.name} </ModalHeader>
        <ModalCloseButton />
        <form
          onSubmit={ev => {
            ev.preventDefault();
            createBlackjackGame();
          }}>
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel htmlFor='bet'>Amount of points to bet</FormLabel>
              <Input
                id='bet'
                placeholder='10'
                name='bet'
                value={bet}
                onChange={e => {
                  const val: number = +e.target.value;
                  if (!isNaN(val)) {
                    setBet(val);
                  } else {
                    toast({
                      title: 'Please enter a valid number',
                      status: 'error',
                    });
                  }
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={toggleLeaderboardOpen}>
              Leaderboard
            </Button>
            <Button colorScheme='blue' mr={3} onClick={createBlackjackGame}>
              Make a bet
            </Button>
            <Button onClick={closeModal}>Cancel</Button>
          </ModalFooter>
        </form>
      </ModalContent>
      <BlackjackLeaderboard
        isOpen={leaderboardOpen}
        close={toggleLeaderboardOpen}></BlackjackLeaderboard>
    </Modal>
  );
}
